import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy, AfterViewInit, SimpleChanges, OnChanges, ChangeDetectionStrategy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CallService } from '@services/core/call.service';
import { OpentokService } from '@services/core/opentok.service';
import { LogService } from '@services/core/log.service';
import { FileShareService } from '@services/other/file-share.service';
import { ArCollaborationService } from '@services/core/ar-collaboration.service';
import { FullscreenService } from '@services/support/fullscreen.service';
import { SubscriberService } from '@services/core/subscriber.service';
import { DbService } from '@services/core/db.service';
import { RecordingService } from '@services/recording/recording.service';
import { DiagnosticsService } from '@services/core/diagnostics.service';
import { environment } from 'environments/environment';

//Rxjs imports
import { Subscription, interval, fromEvent } from 'rxjs';
import { map, first } from 'rxjs/operators';

//Models imports
import { Frame } from '@models/Frame';
import { UploadFile } from '@models/UploadFile';
import { ArDot } from '@models/ArDot';
import { parseConnectionData } from '@models/MediaPublication';
import { AuthService } from '@services/core/auth.service';
import { RoomSessionService } from '@services/core/room-session.service';
import { UtilityService } from '@services/support/utility.service';

@Component({
    selector: 'app-publisher',
    templateUrl: './publisher.component.html',
    styleUrls: ['./publisher.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PublisherComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @ViewChild('publisherContainer', { static: true }) publisherContainer: ElementRef;
  @Input('frame') frame: Frame;
  @Input('size') size: string;
  @Input('nohover') nohover: boolean = false;
  @Input('isMobileScreen') isMobileScreen: boolean = false;

  @Output() toggleFullscreen = new EventEmitter<boolean>();

  showPlaceholderBg: boolean = true;

  publisher: OT.Publisher;
  publisherData: any = null;

  cameraSub: Subscription = null;

  screenPublishing: boolean = false;
  screenSub: Subscription = null;

  signalState: number = 0;
  audioKbps: number = 0;
  videoKbps: number = 0;

  differenceVideoPacketLossRatio: number = 0;
  differenceAudioPacketLossRatio: number = 0;

  videoPacketLossRatio: number = 0;
  audioPacketLossRatio: number = 0;

  minStatsVideo: number = 0;
  minStatsAudio: number = 0;

  listenStatsSub: Subscription = null;

  showStatsDetails: boolean = false;

  screenWidth: number = 0;
  screenHeight: number = 0;
  screenWidthDynamic: number = 0;
  screenHeightDynamic: number = 0;

  //BP: Break Point
  minBPVideoLossRatio: number = 0;
  maxBPVideoLossRatio: number = 0;
  minBPAudioLossRatio: number = 0;
  maxBPAudioLossRatio: number = 0;

  audioBPMax: number = 0;
  audioBPMid: number = 0;
  audioBPMin: number = 0;

  logsSub: Subscription = null;
  currentOriginalText: any = null;
  currentTranslation: any = null;

  fullscreenState: { fullscreen: boolean, supported: boolean } = { fullscreen: false, supported: true };
  fullscreenSub: Subscription = null;

  audioAvailable: boolean = false;
  private publishAudioSub: Subscription = null;
  videoAvailable: boolean = false;
  private publishVideoSub: Subscription = null;

  collaborationRequestSub: Subscription = null;

  arDots: ArDot[] = [];
  arCollSub: Subscription = null;

  pipEnabled: boolean = false;
  _document: any = document;

  continueToTryPublish: boolean = true;

  audioLevelIndicatorStyle = { width: "0px" };
  movingAvg = null;

  addOnsSub: Subscription = null;

  audioDevices: any[] = [];

  publisherMuted: boolean = false;

  cameraMirror: boolean = false;
  private cameraMirrorSub: Subscription = null;

  private subtitleSub: Subscription = null;
  private roomUserStatusSub: Subscription = null;
  private recordingStoppedByUserCount: boolean = false;

  private readonly destroyRef = inject(DestroyRef);
  private readonly isIOS = inject(UtilityService).getBrowserParser().is('iOS');
  private videoReattachSub: Subscription;

  constructor(
    public opentokService: OpentokService,

    private roomSessionService: RoomSessionService,
    private callService: CallService,
    private logService: LogService,
    private fileShareService: FileShareService,
    private arCollaborationService: ArCollaborationService,
    private changeDetector: ChangeDetectorRef,
    private fullscreenService: FullscreenService,
    private subscribeService: SubscriberService,
    private dbService: DbService,
    private authService: AuthService,
    private recordingService: RecordingService,
    private diagnosticsService: DiagnosticsService
  ) { }

  private debugAudioLog(step: string, data: any = {}) {
    if (environment.production) {
      return;
    }

    console.log("[OT-AUDIO][PUBLISHER]", step, {
      roomId: this.roomSessionService.currentRoomId,
      vsSessionId: this.roomSessionService.currentSessionId,
      userId: this.authService.currentUser?.id,
      frameType: this.frame?.type,
      frameUid: this.frame?.uid,
      publisherStreamId: this.publisher?.stream?.streamId,
      publisherHasAudio: this.publisher?.stream?.hasAudio,
      publisherHasVideo: this.publisher?.stream?.hasVideo,
      audioAvailable: this.audioAvailable,
      videoAvailable: this.videoAvailable,
      publisherMuted: this.publisherMuted,
      ...data
    });
  }

  ngOnInit() {

    // Global OpenTok element tracker
    this.setupGlobalOTElementTracker();

    /*
        this.subtitleSub = this.dbService.listen<string>(`accounts/${this.authService.currentUser.account_id}/sessions/${this.roomSessionService.currentRoomId}/${this.roomSessionService.currentSessionId}/transcription/${this.authService.currentUser.id}`)
        .subscribe(text => {
          if (text) {
            this.subtitle = text.length > 60 ? "..." + text.substring(text.length - 57) : text
          }
        })
    */
    this.fullscreenSub = this.fullscreenService.isFullscreen.subscribe(state => {
      this.fullscreenState = state;
      this.changeDetector.detectChanges();
    });

    this.addOnsSub = this.authService.addOns.subscribe(addOns => {
      if (addOns) {
        this.showStatsDetails = addOns.detailedvideoquality;
      }
    });

    if (this._document.pictureInPictureEnabled) {
      this.pipEnabled = true;
    }
    this.cameraMirrorSub = this.opentokService.cameraMirror.subscribe(mirror => {
      this.cameraMirror = mirror;
      this.changeDetector.detectChanges();
    });

    this.collaborationRequestSub = this.logService.onSreenshotRequest.subscribe(message => {
      const imgData = this.publisher.getImgData();
      this.sendSnapshot(imgData);
    });

    this.audioDevices = this.opentokService.getAvailableDevices().filter(d => d.kind === "audioInput");

    this.screenSub = this.opentokService.screenPublishState.subscribe(state => {
      this.screenPublishing = state;
      this.changeDetector.detectChanges();

      if (this.screenPublishing && this._document.pictureInPictureElement && this._document.exitPictureInPicture) {
        this._document.exitPictureInPicture();
      }

      // The mic rides on the screen publisher while sharing (mobile clients
      // only play audio from the screen stream), so the level indicator
      // follows it. Stats stay on the camera publisher, which keeps its video.
      // Only attach when the screen publisher actually has an audio track;
      // otherwise OpenTok's level sampler crashes ("undefined ... 'sample'").
      if (this.frame && this.frame.type === "publisher" && this.audioDevices.length !== 0) {
        const screenPublisher = this.opentokService.currentScreenPublisher;
        if (this.screenPublishing) {
          if (this.publisher) {
            this.publisher.off("audioLevelUpdated", this.audioLevelUpdated);
          }
          if (screenPublisher && screenPublisher.getAudioSource()) {
            screenPublisher.on("audioLevelUpdated", this.audioLevelUpdated);
          }
        } else {
          if (screenPublisher) {
            screenPublisher.off("audioLevelUpdated", this.audioLevelUpdated);
          }
          if (this.publisher) {
            this.publisher.off("audioLevelUpdated", this.audioLevelUpdated);
            this.publisher.on("audioLevelUpdated", this.audioLevelUpdated);
          }
        }
      }
      this.debugAudioLog("screen-publish-state-changed", {
        screenPublishing: this.screenPublishing,
        screenPublisherStreamId: this.opentokService.currentScreenPublisher?.stream?.streamId,
        screenPublisherHasAudioTrack: !!this.opentokService.currentScreenPublisher?.getAudioSource?.()
      });
    });

    this.publishAudioSub = this.opentokService.publishAudio.subscribe(publishAudio => {
      this.publisherMuted = !publishAudio;
      this.debugAudioLog("publish-audio-observable", {
        publishAudio,
        publisherStreamId: this.publisher?.stream?.streamId,
        publisherStreamHasAudio: this.publisher?.stream?.hasAudio
      });
      if (!publishAudio) {
        this.audioAvailable = false;
      }
    });
    this.publishVideoSub = this.opentokService.publishVideo.subscribe(publishVideo => {
      if (!publishVideo) {
        this.videoAvailable = false;
      }
    });

    let timer = null
    const date = Date.now()
    let lastTranscriptionAt = 0
    let transcriptionCount = 0
    this.logsSub = this.dbService.querySnap<any>(`accounts/${this.authService.currentUser.account_id}/sessions/${this.roomSessionService.currentRoomId}/${this.roomSessionService.currentSessionId}/transcriptions`, [{ key: "orderByKey", value: null }, { key: "limitToLast", value: 1 }], "child_added")
      .subscribe(snp => {
        const log = snp.data;
        log.id = snp.key;

        if (this.authService.currentUser.id === log.owner && Date.now() - date > 500) {
          const now = Date.now();
          transcriptionCount += 1;
          const gapMs = lastTranscriptionAt ? (now - lastTranscriptionAt) : null;
          lastTranscriptionAt = now;
          this.debugAudioLog("transcription-received", {
            transcriptionCount,
            gapMs,
            owner: log.owner,
            contentLen: log.content ? String(log.content).length : 0,
            translationLen: log.translation ? String(log.translation).length : 0,
            contentPreview: log.content ? String(log.content).slice(0, 80) : null,
            translationPreview: log.translation ? String(log.translation).slice(0, 80) : null,
            gapSuspected: gapMs != null && gapMs > 15000
          });

          this.currentTranslation = log.translation;
          this.currentOriginalText = log.content;
          const duration = Math.min(Math.max(this.currentTranslation.length * 100, 1000), 4000);
          const logMessage = this.currentTranslation.length * 200;
          console.log(logMessage);
          this.diagnosticsService.logMessage(`Translation display duration: ${logMessage}`, 'info', {
            translationLength: this.currentTranslation.length,
            duration: logMessage,
            sessionId: this.roomSessionService?.currentSessionId
          });

          if (timer) { clearTimeout(timer) }
          timer = setTimeout(() => {
            this.currentTranslation = null;
            this.currentOriginalText = null;
            timer = null;
          }, duration);
        }
      });

    // Monitor room user status to stop recording if user count exceeds 2
    this.roomUserStatusSub = this.roomSessionService.currentRoom.subscribe(room => {
      if (room && (room as any).room_data && (room as any).room_data.users) {
        const activeUsers = (room as any).room_data.users.filter((user: any) => user.in_room);
        this.checkUserCountAndStopRecording(activeUsers.length);
      }
    });
  }

  audioLevelUpdated = (event: OT.Event<"audioLevelUpdated", OT.Publisher> & { audioLevel: number; }) => {
    if (this.frame.style2) {
      if (this.movingAvg === null || this.movingAvg <= event.audioLevel) {
        this.movingAvg = event.audioLevel;
      } else {
        this.movingAvg = 0.7 * this.movingAvg + 0.3 * event.audioLevel;
      }

      // 1.5 scaling to map the -30 - 0 dBm range to [0,1]
      var logLevel = (Math.log(this.movingAvg) / Math.LN10) / 1.5 + 1;
      logLevel = Math.min(Math.max(logLevel, 0), 1);
      this.audioLevelIndicatorStyle.width = Math.floor(logLevel * 100) + "px";
      if (logLevel > 0.4) {
        this.frame.style2["box-shadow"] = `0px 0px 4px 4px rgb(69 130 179)`;
      } else if (logLevel > 0.2) {
        this.frame.style2["box-shadow"] = `0px 0px 4px 2px rgb(69 130 179)`;
      } else {
        this.frame.style2["box-shadow"] = '0px 3px 6px rgb(0, 0, 0)';
      }
    }
  }

  listenStats(publisher: OT.Publisher) {

    this.signalState = 0; //0 --> bad, 1 --> ok, 2 --> good, 3 --> excellent

    this.minBPAudioLossRatio = 0.005;
    this.maxBPAudioLossRatio = 0.05;
    this.minBPVideoLossRatio = 0.005;
    this.maxBPVideoLossRatio = 0.03;

    const lastStats: any = {}
    return interval(5000).subscribe(() => {
      publisher.getStats((statsError, stats) => {
        if (statsError) {
          console.log(statsError);
          this.debugAudioLog("stats-error", {
            errorName: statsError.name,
            errorMessage: statsError.message
          });
          this.diagnosticsService.logError('Stats error in publisher', statsError, {
            component: 'PublisherComponent',
            method: 'getStats',
            sessionId: this.roomSessionService?.currentSessionId
          });
          return;
        }

        if (!this.dbService.connected()) {
          this.videoAvailable = false;
          this.audioAvailable = false;

          this.signalState = 0;
          this.minStatsVideo = 0;
          this.minStatsAudio = 0;
          this.audioPacketLossRatio = 0;
          this.videoPacketLossRatio = 0;
          return;
        }

        this.screenWidth = publisher.stream.videoDimensions.width;
        this.screenHeight = publisher.stream.videoDimensions.height;
        this.screenWidthDynamic = publisher.videoWidth();
        this.screenHeightDynamic = publisher.videoHeight();

        this.videoAvailable = publisher.stream.hasVideo;
        this.audioAvailable = publisher.stream.hasAudio;

        let timestamp = 0;
        this.minStatsAudio = 10000;
        this.minStatsVideo = 10000;

        const statMap: any = {};
        stats.forEach(stat => {
          statMap[stat.subscriberId] = stat;
          if (!lastStats[stat.subscriberId]) {
            lastStats[stat.subscriberId] = {
              lastVideoPacketsLost: 0,
              lastVideoPacketsSent: 0,
              lastAudioPacketsLost: 0,
              lastAudioPacketsSent: 0,
              lastAudioBytesSent: 0,
              lastVideoBytesSent: 0,
              lastTimestamp: 0
            }
          }
        });

        const lastSubscriberIds = Object.keys(lastStats);
        for (const sId of lastSubscriberIds) {
          const subscriberDropped = lastStats[sId] && !statMap[sId]
          if (subscriberDropped) {
            delete lastStats[sId]
          }
          //const newSubscriber = !lastStats[sId] && statMap[sId];
        }

        const subscriberIds = Object.keys(statMap);
        for (const sId of subscriberIds) {
          let stat = statMap[sId];
          let lastStat = lastStats[sId];
          timestamp = stat.stats.timestamp / 1000;
          if (lastStat.lastTimestamp !== 0) {

            stat.videoPacketsSent = stat.stats.video?.packetsLost + stat.stats.video?.packetsSent;
            stat.videoPacketsLost = stat.stats.video?.packetsLost;

            stat.videoPacketsSentDifference = stat.videoPacketsSent - lastStat.lastVideoPacketsSent;
            stat.videoPacketsLostDifference = stat.videoPacketsLost - lastStat.lastVideoPacketsLost;

            stat.audioPacketsSent = stat.stats.audio?.packetsLost + stat.stats.audio?.packetsSent;
            stat.audioPacketsLost = stat.stats.audio?.packetsLost;

            stat.audioPacketsSentDifference = stat.audioPacketsSent - lastStat.lastAudioPacketsSent;
            stat.audioPacketsLostDifference = stat.audioPacketsLost - lastStat.lastAudioPacketsLost;

            this.videoKbps = Math.floor(8 * (stat.stats.video?.bytesSent - lastStat.lastVideoBytesSent) / (1024 * (timestamp - lastStat.lastTimestamp)));
            if (this.videoKbps < this.minStatsVideo && (this.videoKbps !== 0)) {
              this.minStatsVideo = this.videoKbps;
              if (stat.videoPacketsSentDifference > 0) {
                this.videoPacketLossRatio = parseFloat((stat.videoPacketsLostDifference / stat.videoPacketsSentDifference).toFixed(4));
              } else {
                this.videoPacketLossRatio = 0;
              }
            }

            this.audioKbps = Math.floor(8 * (stat.stats.audio?.bytesSent - lastStat.lastAudioBytesSent) / (1024 * (timestamp - lastStat.lastTimestamp)));
            if (this.audioKbps < this.minStatsAudio && (this.audioKbps !== 0)) {
              this.minStatsAudio = this.audioKbps;
              if (stat.audioPacketsSentDifference > 0) {
                this.audioPacketLossRatio = parseFloat((stat.audioPacketsLostDifference / stat.audioPacketsSentDifference).toFixed(4));
              } else {
                this.audioPacketLossRatio = 0;
              }
            }

            lastStat.lastVideoPacketsSent = stat.videoPacketsSent;
            lastStat.lastVideoPacketsLost = stat.videoPacketsLost;

            lastStat.lastAudioPacketsSent = stat.audioPacketsSent;
            lastStat.lastAudioPacketsLost = stat.audioPacketsLost;
          }

          lastStat.lastAudioBytesSent = stat.stats.audio?.bytesSent;
          lastStat.lastVideoBytesSent = stat.stats.video?.bytesSent;
          lastStat.lastTimestamp = timestamp;
        }

        this.debugAudioLog("stats-snapshot", {
          subscriberCount: subscriberIds.length,
          minStatsAudio: this.minStatsAudio,
          minStatsVideo: this.minStatsVideo,
          audioKbps: this.audioKbps,
          videoKbps: this.videoKbps,
          audioPacketLossRatio: this.audioPacketLossRatio,
          videoPacketLossRatio: this.videoPacketLossRatio,
          publisherStreamHasAudio: publisher.stream?.hasAudio,
          publisherStreamHasVideo: publisher.stream?.hasVideo,
          dbConnected: this.dbService.connected()
        });

        let breakpoints = {
          max: { res1920x1080: 0, res1280x720: 0, res640x480: 0, res352x288: 0, res320x240: 0 },
          mid: { res1920x1080: 0, res1280x720: 0, res640x480: 0, res352x288: 0, res320x240: 0 },
          min: { res1920x1080: 0, res1280x720: 0, res640x480: 0, res352x288: 0, res320x240: 0 }
        }
        if (!this.screenPublishing) {
          breakpoints = {
            max: {
              res1920x1080: 1000,
              res1280x720: 1000,
              res640x480: 600,
              res352x288: 300,
              res320x240: 300
            },
            mid: {
              res1920x1080: 675,
              res1280x720: 675,
              res640x480: 425,
              res352x288: 225,
              res320x240: 225
            },
            min: {
              res1920x1080: 350,
              res1280x720: 350,
              res640x480: 250,
              res352x288: 150,
              res320x240: 150
            }
          }
        } else {
          breakpoints = {
            max: {
              res1920x1080: 500,
              res1280x720: 300,
              res640x480: 150,
              res352x288: 100,
              res320x240: 100
            },
            mid: {
              res1920x1080: 300,
              res1280x720: 150,
              res640x480: 100,
              res352x288: 50,
              res320x240: 50
            },
            min: {
              res1920x1080: 100,
              res1280x720: 50,
              res640x480: 50,
              res352x288: 25,
              res320x240: 25
            }
          }
        }

        //Calculating signal state only
        if (this.screenPublishing || this.videoAvailable) {
          if (this.screenWidth * this.screenHeight >= 1920 * 1080) {
            if (this.minStatsVideo > breakpoints.max.res1920x1080) {
              if (this.videoPacketLossRatio < this.minBPVideoLossRatio) {
                this.signalState = 3;
              } else if (this.videoPacketLossRatio >= this.minBPVideoLossRatio) {
                this.signalState = 2;
              }
            }
            else if (this.minStatsVideo > breakpoints.mid.res1920x1080 && this.minStatsVideo <= breakpoints.max.res1920x1080) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 2;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 1;
              }
            }
            else if (this.minStatsVideo > breakpoints.min.res1920x1080 && this.minStatsVideo <= breakpoints.mid.res1920x1080) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 1;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 0;
              }
            }
            else if (this.minStatsVideo <= breakpoints.min.res1920x1080) {
              this.signalState = 0;
            }
          }
          else if (this.screenWidth * this.screenHeight >= 1280 * 720 && this.screenWidth * this.screenHeight < 1920 * 1080) {
            if (this.minStatsVideo > breakpoints.max.res1280x720) {
              if (this.videoPacketLossRatio < this.minBPVideoLossRatio) {
                this.signalState = 3;
              } else if (this.videoPacketLossRatio >= this.minBPVideoLossRatio) {
                this.signalState = 2;
              }
            }
            else if (this.minStatsVideo > breakpoints.mid.res1280x720 && this.minStatsVideo <= breakpoints.max.res1280x720) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 2;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 1;
              }
            }
            else if (this.minStatsVideo > breakpoints.min.res1280x720 && this.minStatsVideo <= breakpoints.mid.res1280x720) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 1;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 0;
              }
            }
            else if (this.minStatsVideo <= breakpoints.min.res1280x720) {
              this.signalState = 0;
            }
          }
          else if (this.screenWidth * this.screenHeight >= 640 * 480 && this.screenWidth * this.screenHeight < 1280 * 720) {
            if (this.minStatsVideo > breakpoints.max.res640x480) {
              if (this.videoPacketLossRatio < this.minBPVideoLossRatio) {
                this.signalState = 3;
              } else if (this.videoPacketLossRatio >= this.minBPVideoLossRatio) {
                this.signalState = 2;
              }
            }
            else if (this.minStatsVideo > breakpoints.mid.res640x480 && this.minStatsVideo <= breakpoints.max.res640x480) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 2;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 1;
              }
            }
            else if (this.minStatsVideo > breakpoints.min.res640x480 && this.minStatsVideo <= breakpoints.mid.res640x480) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 1;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 0;
              }
            }
            else if (this.minStatsVideo <= breakpoints.min.res640x480) {
              this.signalState = 0;
            }
          }
          else if (this.screenWidth * this.screenHeight >= 352 * 288 && this.screenWidth * this.screenHeight < 640 * 480) {
            if (this.minStatsVideo > breakpoints.max.res352x288) {
              if (this.videoPacketLossRatio < this.minBPVideoLossRatio) {
                this.signalState = 3;
              } else if (this.videoPacketLossRatio >= this.minBPVideoLossRatio) {
                this.signalState = 2;
              }
            }
            else if (this.minStatsVideo > breakpoints.mid.res352x288 && this.minStatsVideo <= breakpoints.max.res352x288) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 2;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 1;
              }
            }
            else if (this.minStatsVideo > breakpoints.min.res352x288 && this.minStatsVideo <= breakpoints.mid.res352x288) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 1;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 0;
              }
            }
            else if (this.minStatsVideo <= breakpoints.min.res352x288) {
              this.signalState = 0;
            }
          }
          else if (this.screenWidth * this.screenHeight >= 320 * 240 && this.screenWidth * this.screenHeight < 352 * 288) {
            if (this.minStatsVideo > breakpoints.max.res320x240) {
              if (this.videoPacketLossRatio < this.minBPVideoLossRatio) {
                this.signalState = 3;
              } else if (this.videoPacketLossRatio >= this.minBPVideoLossRatio) {
                this.signalState = 2;
              }
            }
            else if (this.minStatsVideo > breakpoints.mid.res320x240 && this.minStatsVideo <= breakpoints.max.res320x240) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 2;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 1;
              }
            }
            else if (this.minStatsVideo > breakpoints.min.res320x240 && this.minStatsVideo <= breakpoints.mid.res320x240) {
              if (this.videoPacketLossRatio < this.maxBPVideoLossRatio) {
                this.signalState = 1;
              } else if (this.videoPacketLossRatio >= this.maxBPVideoLossRatio) {
                this.signalState = 0;
              }
            }
            else if (this.minStatsVideo <= breakpoints.min.res320x240) {
              this.signalState = 0;
            }
          }
        }
        else if (!this.screenPublishing && !this.videoAvailable) {
          this.minStatsVideo = 0;
          this.videoPacketLossRatio = 0;
        }

        if (!this.screenPublishing && !this.videoAvailable && this.audioAvailable) {
          this.audioBPMax = 20;
          this.audioBPMid = 15;
          this.audioBPMin = 10;
          if (this.minStatsAudio > this.audioBPMax) {
            if (this.audioPacketLossRatio < this.minBPAudioLossRatio) {
              this.signalState = 3;
            } else if (this.audioPacketLossRatio >= this.minBPAudioLossRatio) {
              this.signalState = 2;
            }
          }
          else if (this.minStatsAudio > this.audioBPMid && this.minStatsAudio <= this.audioBPMax) {
            if (this.audioPacketLossRatio < this.maxBPAudioLossRatio) {
              this.signalState = 2;
            } else if (this.audioPacketLossRatio >= this.maxBPAudioLossRatio) {
              this.signalState = 1;
            }
          }
          else if (this.minStatsAudio > this.audioBPMin && this.minStatsAudio <= this.audioBPMid) {
            if (this.audioPacketLossRatio < this.maxBPAudioLossRatio) {
              this.signalState = 1;
            } else if (this.audioPacketLossRatio >= this.maxBPAudioLossRatio) {
              this.signalState = 0;
            }
          }
          else if (this.minStatsAudio <= this.audioBPMin) {
            this.signalState = 0;
          }
        }
        else if (!this.audioAvailable) {
          this.minStatsAudio = 0;
          this.audioPacketLossRatio = 0;
        }

        if (!this.publisherData) {
          this.publisherData = parseConnectionData(publisher.stream.connection?.data);
        }
        this.subscribeService.setPublisherStats(this.publisherData.uid, {
          audio: this.minStatsAudio,
          video: this.minStatsVideo,
          signalState: this.signalState,
          videoPacketLossRatio: this.videoPacketLossRatio,
          audioPacketLossRatio: this.audioPacketLossRatio
        });
      })
    })
  }

  async sendSnapshot(imgData: string) {
    const d = new Date();
    const dateString = d.getFullYear() + "-" +
      ("0" + (d.getMonth() + 1)).slice(-2) + "-" +
      ("0" + d.getDate()).slice(-2) + "_" +
      ("0" + d.getHours()).slice(-2) + "-" +
      ("0" + d.getMinutes()).slice(-2) + "-" +
      ("0" + d.getSeconds()).slice(-2) + "-" +
      d.getMilliseconds()

    // First try to convert PNG to JPEG
    const jpegFilename = "VS_" + dateString + '.jpg';
    let file = await this.fileShareService.convertBase64toJpeg('data:image/png;base64,' + imgData, this.publisher.videoWidth(), this.publisher.videoHeight(), 0.9, jpegFilename);
    let upload: UploadFile;
    if (file) {
      // If conversion succeeded, create upload object
      upload = {
        file: file,
        extension: "jpg",
        file_type: 'image',
        progress: 0
      }
    } else {
      // If conversion fails, upload PNG file
      const pngFilename = "VS_" + dateString + '.png';
      file = this.fileShareService.convertBase64toFile(imgData, 'image/png', pngFilename);
      upload = {
        file: file,
        extension: "png",
        file_type: 'image',
        progress: 0
      }
    }
    return this.fileShareService.uploadSnapshot(upload)
      .then(up1 => this.fileShareService.saveUserFileToChatDb(this.fileShareService.createDatabaseUserFile(up1)))
      .then(up2 => this.logService.sendShareScreenshotLog(up2));
  }

  ngAfterViewInit() {
    if (this.frame && this.frame.type === "publisher2") {
      // Hide OpenTok elements for translation streams on Safari mobile
      this.hideOpenTokElementsForTranslation();
      return;
    }

    try {
      // Check if the user is the owner expert of the room
      this.roomSessionService.currentRoom.pipe(first()).toPromise()
        .then(room => {
          if (room && room.room_data && room.room_data.owner_expert === this.authService.currentUser.id) {
            // Initialize video recording service for subscribers
            this.recordingService.initForPublisher(this.roomSessionService, this.opentokService);
          } else {
            console.log('🛑 Not starting publisher recording as user is not the owner expert.');
            this.diagnosticsService.logMessage('Not starting publisher recording as user is not the owner expert', 'info', {
              reason: 'not_owner_expert',
              userId: this.authService?.currentUser?.id,
              roomId: this.roomSessionService?.currentRoomId,
              sessionId: this.roomSessionService?.currentSessionId
            });
          }
        })
    } catch (error) {
      console.error('Error checking owner expert:', error);
      this.diagnosticsService.logError('Error checking owner expert', error, {
        component: 'PublisherComponent',
        method: 'ngAfterViewInit',
        action: 'checking_owner_expert',
        sessionId: this.roomSessionService?.currentSessionId
      });
    }

    this.cameraSub = this.opentokService.cameraPublishRequest.subscribe(() => {
      // FIX: Check if publisher already exists to prevent duplicates
      const hasExistingPublisher = this.publisher && this.publisher.stream;
      const hasOpentokPublisher = this.opentokService.currentCameraPublisher && this.opentokService.currentCameraPublisher.stream;

      this.debugAudioLog("camera-publish-request", {
        hasExistingPublisher,
        hasOpentokPublisher,
        opentokPublisherStreamId: this.opentokService.currentCameraPublisher?.stream?.streamId,
        opentokPublisherHasAudio: this.opentokService.currentCameraPublisher?.stream?.hasAudio
      });

      if (hasExistingPublisher || hasOpentokPublisher) {
        this.debugAudioLog("camera-publish-request-skipped-existing");
        return;
      }

      this.showPlaceholderBg = false;
      
      
      this.tryToPublish(3)
        .catch(error => {
          // Catch opentok errors first
          if (error) {
            this.debugAudioLog("camera-publish-request-failed", {
              errorName: error.name,
              errorCode: error.code,
              errorMessage: error.message
            });
            this.callService.otPublisherFailed(error.name, error.code);
          }
          
          
          return null;
        })
        .then(publisher => {
          if (publisher) {
            this.publisher = publisher;
            this.debugAudioLog("camera-publish-request-succeeded", {
              streamId: publisher.stream?.streamId,
              streamHasAudio: publisher.stream?.hasAudio,
              streamHasVideo: publisher.stream?.hasVideo
            });
            
            
            //Publisher data fullfills
            this.initializePublisherListeners();

            this.subscribeService.setPublisherStats(this.publisherData.uid, null);

            if (this.listenStatsSub) { this.listenStatsSub.unsubscribe() }
            this.listenStatsSub = this.listenStats(publisher);


          }
        })
        .catch(error => {
          // Catch errors in then block
          console.log(error);
          this.diagnosticsService.logError('Publisher then block error', error, {
            errorMessage: error?.message,
            sessionId: this.roomSessionService?.currentSessionId
          });
        });
    });
  }


  async tryToPublish(maxTry: number) {
    let publisher = null;
    let error = null;
    let retryCount = 0;
    while (this.continueToTryPublish && !publisher && retryCount < maxTry) {
      this.debugAudioLog("try-publish-attempt", {
        attempt: retryCount + 1,
        maxTry
      });
      publisher = await (this.opentokService.startCameraPublish(this.publisherContainer)
        .catch((err: OT.OTError) => {
          this.opentokService.saveOtError("publisher", err);
          error = err;
          this.debugAudioLog("try-publish-attempt-error", {
            attempt: retryCount + 1,
            errorName: err.name,
            errorCode: (<any>err).code,
            errorMessage: err.message,
            retryRequired: this.isRetryRequired(err.name)
          });
          if (!this.isRetryRequired(err.name)) {
            retryCount = maxTry;
          }
          return null;
        }));
      retryCount = retryCount + 1;
    }
    if (this.continueToTryPublish && !publisher) {
      throw error;
    }
    return publisher;
  }

  isRetryRequired(errorName: string) {
    return errorName === 'OT_CHROME_MICROPHONE_ACQUISITION_ERROR' ||
      errorName === 'OT_CONSTRAINTS_NOT_SATISFIED' ||
      errorName === 'OT_CREATE_PEER_CONNECTION_FAILED' ||
      errorName === 'OT_ICE_WORKFLOW_FAILED' ||
      errorName === 'OT_MEDIA_ERR_ABORTED' ||
      errorName === 'OT_MEDIA_ERR_DECODE' ||
      errorName === 'OT_MEDIA_ERR_NETWORK' ||
      errorName === 'OT_MEDIA_ERR_SRC_NOT_SUPPORTED' ||
      errorName === 'OT_NO_VALID_CONSTRAINTS' ||
      errorName === 'OT_NOT_CONNECTED' ||
      errorName === 'OT_NOT_SUPPORTED' ||
      errorName === 'OT_PERMISSION_DENIED' ||
      errorName === 'OT_SET_REMOTE_DESCRIPTION_FAILED' ||
      errorName === 'OT_STREAM_CREATE_FAILED' ||
      errorName === 'OT_TIMEOUT' ||
      errorName === 'OT_USER_MEDIA_ACCESS_DENIED' ||
      errorName === 'OT_UNEXPECTED_SERVER_RESPONSE' ||
      errorName === 'OT_INVALID_PARAMETER' ||
      errorName === 'OT_MEDIA_ERR_ABORTED' ||
      errorName === 'OT_MEDIA_ERR_DECODE' ||
      errorName === 'OT_MEDIA_ERR_NETWORK' ||
      errorName === 'OT_MEDIA_ERR_SRC_NOT_SUPPORTED' ||
      errorName === 'OT_NOT_SUPPORTED'
  }

  initializePublisherListeners() {
    this.publisherData = parseConnectionData(this.publisher.stream.connection?.data);
    this.changeDetector.detectChanges();

    this.debugAudioLog("publisher-listeners-initialized", {
      publisherData: this.publisherData,
      audioDeviceCount: this.audioDevices.length,
      streamId: this.publisher.stream?.streamId,
      streamHasAudio: this.publisher.stream?.hasAudio,
      streamHasVideo: this.publisher.stream?.hasVideo,
      streamVideoType: this.publisher.stream?.videoType
    });


    if (this.audioDevices.length !== 0) {
      this.publisher.on("audioLevelUpdated", this.audioLevelUpdated)
    }

    this.videoAvailable = this.publisher.stream.hasVideo;
    this.audioAvailable = this.publisher.stream.hasAudio;

    if (this.frame && this.frame.type === "publisher2") {
      // Hide OpenTok elements for translation streams after publisher is initialized
      this.hideOpenTokElementsForTranslation();
      return;
    }

    this.reattachVideoOnFirstFrame(this.publisherContainer?.nativeElement.querySelector('.OT_video-element'));
    this.publisher.on('videoElementCreated', event => this.reattachVideoOnFirstFrame(event.element));

    if (this.arCollSub) { this.arCollSub.unsubscribe() }
    this.arCollSub = this.arCollaborationService.getPublisherDots(this.publisherData.uid)
      .pipe(
        map(dots => {
          return !dots ? [] :
            Object.values(dots).filter(x => x !== '')
              .map((x: string) => {
                const values = x.split('!');
                let left = this.cameraMirror ? (100 - parseFloat(values[1])) : parseFloat(values[1]);
                let top = parseFloat(values[2]);
                let temp_left = left + "%";
                let temp_top = top + "%";

                const arDot: ArDot = {
                  colorCode: values[0],
                  left: temp_left,
                  top: temp_top,
                  name: values[3],
                  shape: values[4]
                }
                return arDot;
              })
        })
      )
      .subscribe(dots => {
        this.arDots = dots.map(dot => {
          dot.color = ArCollaborationService.arColorNames[dot.colorCode];
          return dot;
        });
      });
  }

  private reattachVideoOnFirstFrame(element: Element | null) {
    this.videoReattachSub?.unsubscribe();
    if (!this.isIOS || !(element instanceof HTMLVideoElement)) {
      return;
    }
    if (element.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.reattachVideoStream(element);
      return;
    }
    this.videoReattachSub = fromEvent(element, 'loadeddata')
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reattachVideoStream(element));
  }

  private reattachVideoStream(video: HTMLVideoElement) {
    const stream = video.srcObject;
    if (!stream) {
      return;
    }
    video.srcObject = null;
    video.srcObject = stream;
    video.play().catch(error => this.debugAudioLog("ios-video-reattach-play-failed", { error: error?.name }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.size) {
      this.size = changes.size.currentValue;
    }
    if (changes.isMobileScreen) {
      this.isMobileScreen = changes.isMobileScreen.currentValue;
    }

    // Check if frame changed to translation type
    if (changes.frame && this.frame && this.frame.type === 'publisher2') {
      this.hideOpenTokElementsForTranslation();
    }
  }

  ngOnDestroy() {

    this.continueToTryPublish = false;
    if (this.fullscreenSub) { this.fullscreenSub.unsubscribe(); }
    if (this.cameraSub) { this.cameraSub.unsubscribe(); }
    if (this.screenSub) { this.screenSub.unsubscribe(); }
    if (this.arCollSub) { this.arCollSub.unsubscribe() }
    if (this.publishAudioSub) { this.publishAudioSub.unsubscribe() }
    if (this.publishVideoSub) { this.publishVideoSub.unsubscribe() }
    if (this.collaborationRequestSub) { this.collaborationRequestSub.unsubscribe(); }
    if (this.publisher) {
      this.publisher.off("audioLevelUpdated", this.audioLevelUpdated);
    }
    if (this.logsSub) { this.logsSub.unsubscribe() }
    if (this.listenStatsSub) { this.listenStatsSub.unsubscribe() }
    if (this.addOnsSub) { this.addOnsSub.unsubscribe() }
    if (this.cameraMirrorSub) { this.cameraMirrorSub.unsubscribe() }
    if (this.roomUserStatusSub) { this.roomUserStatusSub.unsubscribe() }
    //if (this.subtitleSub) { this.subtitleSub.unsubscribe() }

    // Cleanup video recording service - but ONLY for the main publisher frame.
    // ngAfterViewInit early-returns for 'publisher2' (translation) frames without ever
    // calling initForPublisher, so their destroy must not tear the root-singleton
    // RecordingService down either: a translation frame destroyed MID-CALL used to stop
    // every active recording and kill the 30s health monitor for the rest of the call.
    if (!this.frame || this.frame.type !== 'publisher2') {
      this.recordingService.ngOnDestroy();
    }

    if (this._document.pictureInPictureElement && this._document.exitPictureInPicture) {
      this._document.exitPictureInPicture();
    }
    this.opentokService.destroyCurrentPublisherIfExists();

    console.log('🔚 [PUBLISHER CLEANUP] Publisher component destroying - recording cleanup handled by RecordingService');
    this.diagnosticsService.logMessage('Publisher component destroying - recording cleanup handled by RecordingService', 'info', {
      sessionId: this.roomSessionService?.currentSessionId
    });
  }

  onToggleFullscreen() {
    if (this.fullscreenState.fullscreen) {
      this.toggleFullscreen.emit(false);
    } else {
      this.toggleFullscreen.emit(true);
    }
  }

  onTogglePictureInPicture() {
    const d: any = document;
    if (d.pictureInPictureElement) {
      if (d.exitPictureInPicture) {
        d.exitPictureInPicture();
      }
    } else {
      const videoElement = this.publisherContainer.nativeElement.getElementsByClassName("OT_video-element").item(0);
      if (videoElement) {
        if (videoElement.requestPictureInPicture) {
          videoElement.requestPictureInPicture()
            .catch(error => {
              // Video failed to enter Picture-in-Picture mode.
            });
        }
      }
    }
  }

  stopSharingScreen() {
    this.opentokService.endScreenSharing();
  }

  private checkUserCountAndStopRecording(userCount: number) {
    if (userCount > 2 && !this.recordingStoppedByUserCount) {
      console.log('🛑 User count exceeds two - client-side recording disabled (handled by RecordingService). Active users:', userCount);
      this.diagnosticsService.logMessage(`User count exceeds two - client-side recording disabled. Active users: ${userCount}`, 'info', {
        reason: 'user_count_exceeded',
        userCount: userCount,
        sessionId: this.roomSessionService?.currentSessionId,
        roomId: this.roomSessionService?.currentRoomId
      });
      
      this.recordingStoppedByUserCount = true;
      console.log('🎯 Server-side recording has taken over');
      this.diagnosticsService.logMessage('Server-side recording has taken over', 'info', {
        sessionId: this.roomSessionService?.currentSessionId
      });
    }
    // Note: Do not reset flag when user count drops - client-side recording stays disabled
  }

  private hideOpenTokElementsForTranslation() {
    // For translation streams (publisher2), hide OpenTok elements
    setTimeout(() => {
      const container = this.publisherContainer?.nativeElement;

      if (container) {
        // Hide all OpenTok elements that might appear
        const openTokElements = container.querySelectorAll(
          '.OT_root, .OT_publisher, .OT_subscriber, .OT_widget-container, ' +
          '.OT_video-element, .OT_video-poster, .OT_video-loading, ' +
          '.OT_name, .OT_archiving, .OT_mute, .OT_audio-level-meter, ' +
          '.OT_video-disabled-indicator, .OT_bar, .OT_edge-bar-item'
        );

        openTokElements.forEach((element) => {
          (element as HTMLElement).style.display = 'none';
          (element as HTMLElement).style.visibility = 'hidden';
          (element as HTMLElement).style.opacity = '0';
        });

        // Also hide elements by ID pattern (OpenTok generates IDs like OT_xxxxx)
        const allElements = container.querySelectorAll('*');
        allElements.forEach(element => {
          if (element.id && element.id.startsWith('OT_')) {
            (element as HTMLElement).style.display = 'none';
            (element as HTMLElement).style.visibility = 'hidden';
            (element as HTMLElement).style.opacity = '0';
          }
        });

        // Keep checking and hiding elements as OpenTok might create them dynamically
        const observer = new MutationObserver(() => {
          const newOpenTokElements = container.querySelectorAll('.OT_root, .OT_publisher, [id^="OT_"]');
          newOpenTokElements.forEach(element => {
            (element as HTMLElement).style.display = 'none';
            (element as HTMLElement).style.visibility = 'hidden';
            (element as HTMLElement).style.opacity = '0';
          });
        });

        observer.observe(container, { childList: true, subtree: true });

        // Clean up observer after 10 seconds
        setTimeout(() => observer.disconnect(), 10000);
      }
    }, 100);

    // Also try hiding after a longer delay in case elements are created later
    setTimeout(() => this.hideOpenTokElementsForTranslation(), 1000);
  }

  private setupGlobalOTElementTracker() {
    // Track all OpenTok elements in the entire document
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;

            // Check if it's an OpenTok element
            if (element.id && element.id.startsWith('OT_')) {
              // Found OpenTok element

              // Only hide if it's specifically a translation element
              const isTranslationElement =
                element.innerHTML.includes('translation') ||
                element.textContent?.includes('translation');

              if (isTranslationElement) {
                (element as HTMLElement).style.setProperty('display', 'none', 'important');
                (element as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
                (element as HTMLElement).style.setProperty('opacity', '0', 'important');
                (element as HTMLElement).style.setProperty('position', 'absolute', 'important');
                (element as HTMLElement).style.setProperty('top', '-9999px', 'important');
                (element as HTMLElement).style.setProperty('left', '-9999px', 'important');
                (element as HTMLElement).style.setProperty('width', '0', 'important');
                (element as HTMLElement).style.setProperty('height', '0', 'important');
                (element as HTMLElement).style.setProperty('overflow', 'hidden', 'important');

                // Also hide all child elements
                const children = element.querySelectorAll('*');
                children.forEach(child => {
                  (child as HTMLElement).style.setProperty('display', 'none', 'important');
                  (child as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
                });
              }
            }

            // Check if it has OpenTok classes
            if (element.className && (element.className.includes('OT_') || element.className.includes('ot-'))) {
              // Silent check for OpenTok class elements
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Global OpenTok element tracker started

    // Also check for existing OpenTok elements and hide them
    this.hideExistingOpenTokElements();

    // Set up periodic check every 5 seconds
    setInterval(() => {
      this.hideExistingOpenTokElements();
    }, 5000);
  }

  private hideExistingOpenTokElements() {
    const existingOTElements = document.querySelectorAll('[id^="OT_"], .OT_root, .OT_publisher, .OT_subscriber');

    existingOTElements.forEach((element, index) => {
      // Only hide if it's specifically a translation element
      const isTranslationElement =
        element.innerHTML.includes('translation') ||
        element.textContent?.includes('translation');

      if (isTranslationElement) {
        (element as HTMLElement).style.setProperty('display', 'none', 'important');
        (element as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
        (element as HTMLElement).style.setProperty('opacity', '0', 'important');
        (element as HTMLElement).style.setProperty('position', 'absolute', 'important');
        (element as HTMLElement).style.setProperty('top', '-9999px', 'important');
        (element as HTMLElement).style.setProperty('left', '-9999px', 'important');
        (element as HTMLElement).style.setProperty('width', '0', 'important');
        (element as HTMLElement).style.setProperty('height', '0', 'important');
        (element as HTMLElement).style.setProperty('overflow', 'hidden', 'important');

        // Also hide all child elements
        const children = element.querySelectorAll('*');
        children.forEach(child => {
          (child as HTMLElement).style.setProperty('display', 'none', 'important');
          (child as HTMLElement).style.setProperty('visibility', 'hidden', 'important');
        });
      }
    });
  }
}
